import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useCallback, useEffect, useMemo } from 'react';
import { HeaderButtons } from './constants';
import HeaderCheckButtons from '@components/checkListV2/headerCheckButtons';
import HeaderCartButtons from '@components/checkListV2/headerCartButtons';
import { setVisible } from '@reducers/optionsMenus';
import { removeSeat } from '@reducers/orders';
import { getAllChecks, showOpenChecks } from '@reducers/storeChecks';
import { removeStoreNotification, acknowledge } from '@reducers/storeNotifications';
import { useNavigate } from 'react-router';
import { logout, setLoggingOut } from '@reducers/oauth';
import getDeviceType from '@utility/deviceType';
import { NOTIFICATION_TYPES } from '@containers/StoreChecks/constants';

const EMPTY_THEME = {};

const Header = ({
  orderingPortraitOrientation,
  serverAppTheme,
  onOpenTable,
  onOpenTab,
  onOpenTogo,
  onOpenSpeedOrder,
  onOpenCounter,
  thirdPartyAppName,
  displayName,
  jobName,
  allChecksVisible,
  storeId,
  getAllChecks,
  removeSeat,
  setVisible,
  showOpenChecks,
  logout,
  setLoggingOut,
  deviceId,
  employeeId,
  employeeCard,
  deviceInformation,
  employeeJobHashTable,
  jobId,
  isSearch,
  openSearch,
  closeSearch,
  searchUpdated,
  notificationTableData,
  notifyLoading,
  removeStoreNotification,
  acknowledge,
  isOnlyOrderMode,
}) => {
  const navigate = useNavigate();

  const headerButtons = useMemo(() => {
    const { isCounter, isDriveThru, isCallCenter } = getDeviceType();
    const deviceGroup = deviceInformation?.deviceGroup;
    const startMode = deviceGroup?.startMode;
    const isDefaultMode = !deviceGroup || startMode === 0;
    const tableTypes = deviceInformation?.deviceGroup?.tableTypes ?? {};
    const isUseJob = tableTypes.useJob ?? true;
    const isCounterMode = isCounter || isDriveThru || isCallCenter;

   if (isCounterMode) {
      return HeaderButtons.map(btn => ({
        ...btn,
        supportOpen: btn.key === 'counter',
      }));
    }

    const serverAppSpeedOrder = !!tableTypes.speedOrder;
    const activeTableTypes = isDefaultMode ? tableTypes : {};
    const jobPerms = employeeJobHashTable?.[jobId] ?? {};

    const supportOpenMap = {
      scan: isDefaultMode && isUseJob
        ? !!(jobId && jobPerms.allowScan)
        : !!activeTableTypes.scan,
      table: isDefaultMode && isUseJob
        ? !jobId || !!(jobId && jobPerms.allowTable)
        : !!activeTableTypes.table,
      tab: isDefaultMode && isUseJob
        ? !jobId || !!(jobId && jobPerms.allowTab)
        : !!activeTableTypes.tab,
      togo: serverAppSpeedOrder,
      counter: false,
      speedOrder: serverAppSpeedOrder,
    };

    return HeaderButtons.map(btn => ({ ...btn, supportOpen: supportOpenMap[btn.key] ?? false }));
  }, [deviceInformation, employeeJobHashTable, jobId]);

  useEffect(() => {
    // pre-fetching logout screen, on header mount
    // to reduce logout screen UI rendering delay.
    import('@containers/CheckIn');
  }, []);

  const handleLogout = useCallback(() => {
    setLoggingOut(true);
    const currentTime = Date.now();
    navigate(`/checkIn?logoutAfterRelaunchInterval=${currentTime}`, {
      replace: true,
    });

    const dataToSend = {
      latitude: '92',
      longitude: '92',
      deviceId,
      employeeId,
      employeeCard,
    };

    setTimeout(() => {
      removeSeat();
      logout(dataToSend);
    }, 0);
  }, [
    deviceId,
    employeeId,
    employeeCard,
    navigate,
    removeSeat,
    logout,
    setLoggingOut,
  ]);

  const filteredNotificationTableData = useMemo(() => {
    const notificationTypeFilters = deviceInformation?.deviceGroup?.notificationTypeFilters;
    if (!notificationTypeFilters) return notificationTableData;
    const typedNotifications = NOTIFICATION_TYPES.map(e => ({
      ...e,
      status: notificationTypeFilters[e.shortName] ?? false,
    }));
    return notificationTableData.filter(
      e => e.enabled && typedNotifications.some(d => e.notificationTypeId === d.id && d.status)
    );
  }, [deviceInformation, notificationTableData]);

  return (
    <div className="p-0 m-0 h-100 d-flex">
      <div className="w-75 d-flex flex-row">
        <HeaderCheckButtons
          headerButtons={headerButtons}
          serverAppTheme={serverAppTheme}
          orderingPortraitOrientation={orderingPortraitOrientation}
          onOpenTable={onOpenTable}
          onOpenTab={onOpenTab}
          onOpenTogo={onOpenTogo}
          onOpenSpeedOrder={onOpenSpeedOrder}
          onOpenCounter={onOpenCounter}
          thirdPartyAppName={thirdPartyAppName}
          displayName={displayName}
          jobName={jobName}
          allChecksVisible={allChecksVisible}
          storeId={storeId}
          getAllChecks={getAllChecks}
          removeSeat={removeSeat}
          setVisible={setVisible}
          showOpenChecks={showOpenChecks}
          isLandScape={!orderingPortraitOrientation}
          notificationTableData={filteredNotificationTableData}
          notifyLoading={notifyLoading}
          removeStoreNotification={removeStoreNotification}
          acknowledge={acknowledge}
          isOnlyOrderMode={isOnlyOrderMode}
        />
      </div>
      <div className="w-25" style={{ position: 'relative', zIndex: 1000 }}>
        <HeaderCartButtons
          serverAppTheme={serverAppTheme}
          orderingPortraitOrientation={orderingPortraitOrientation}
          handleLogout={handleLogout}
          isSearch={isSearch}
          openSearch={openSearch}
          closeSearch={closeSearch}
          searchUpdated={searchUpdated}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({ theme, oauth, optionsMenus, storeNotifications }) => {
  const user = oauth.loggedInUserData;
  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  const displayName =
    fullName.length > 30 ? `${fullName.slice(0, 30)}...` : fullName;
  const jobName =
    user?.jobName ??
    (user?.jobId ? oauth.employeeJobHashTable?.[user.jobId]?.name : undefined);

  return {
    orderingPortraitOrientation: false,
    serverAppTheme: theme.serverAppTheme ?? EMPTY_THEME,
    thirdPartyAppName: oauth.authInfo?.thirdPartyAppName,
    displayName,
    jobName,
    allChecksVisible: optionsMenus.allChecksVisible,
    storeId: oauth.deviceInformation?.storeId,
    employeeId: oauth.loggedInUserData?.externalId,
    employeeCard: oauth.loggedInUserData?.employeeCard,
    deviceInformation: oauth.deviceInformation,
    employeeJobHashTable: oauth.employeeJobHashTable,
    jobId: user?.jobId,
    notificationTableData: storeNotifications.storeNotification,
    notifyLoading: storeNotifications.loading,
    isOnlyOrderMode: oauth.deviceInformation?.deviceGroup?.serverAppSettings?.disableOrdering
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getAllChecks,
      showOpenChecks,
      removeSeat,
      setVisible,
      logout,
      setLoggingOut,
      removeStoreNotification,
      acknowledge,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Header);
